/**
 * Secrets Collective — package protection, opt-out.
 *
 * Three behaviours:
 *   1. Auto-add once there is something else in the bag.
 *   2. Auto-remove when the bag is emptied of real products, so nobody is left
 *      paying for protection on nothing.
 *   3. Respect a decline — once removed by hand it stays off for the session,
 *      otherwise the auto-add would immediately put it back and trap them.
 *
 * Cart writes dispatch a real CartLinesUpdateEvent so the drawer, cart icon and
 * totals all refresh the same way they do for any other change.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const DECLINED_KEY = 'sc-protection-declined';
const OFFERED_KEY = 'sc-protection-offered';

/*
 * Module-level, deliberately not per-element. The drawer re-renders through the
 * Section Rendering API, so more than one <sc-protection> can be mounting at
 * once — and /cart/add.js increments rather than sets, so two overlapping adds
 * bill the customer twice. Everything that writes protection to the cart queues
 * behind this one promise.
 */
let cartWriteLock = Promise.resolve();

/*
 * Our own cart writes dispatch cartLinesUpdate, and this element listens for
 * cartLinesUpdate. Without a guard, removing protection would wake the
 * reconcile that removed it. Held above zero for the whole write.
 */
let selfWrites = 0;

function serialise(work) {
  const run = cartWriteLock.then(work, work);
  // Never let a rejection poison the queue for the next caller.
  cartWriteLock = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

const cartUrl = () => globalThis.Theme?.routes?.cart_url || '/cart';
const addUrl = () => globalThis.Theme?.routes?.cart_add_url || '/cart/add.js';
const changeUrl = () => globalThis.Theme?.routes?.cart_change_url || '/cart/change.js';

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

async function refreshCart() {
  const res = await fetch(`${cartUrl()}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`cart fetch failed: ${res.status}`);
  return res.json();
}

/** Write to the cart and tell the rest of the theme about it. */
async function writeCart(source, url, body, action) {
  selfWrites += 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );

  let ok = true;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...body, sections: sectionIds() }),
    });
    const data = await res.json();
    if (data.status) ok = false;
  } catch {
    ok = false;
  }

  try {
    const cart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: !ok,
        items: cart.items,
        source: 'sc-protection',
        itemCount: cart.item_count,
      },
    });
  } catch (error) {
    deferred.reject?.(error);
  }

  selfWrites -= 1;
  return ok;
}

class SCProtection extends HTMLElement {
  /*
   * The drawer is morphed, so this element survives a cart update while the
   * switch inside it is replaced by fresh markup. A listener bound to the old
   * switch dies with it, and connectedCallback does not fire again because the
   * host was never disconnected — the switch then looks fine and does nothing.
   * Delegating to the host, which survives, is what keeps it alive.
   *
   * The old guard wrote data-bound as an attribute, which the morph also
   * preserved, so it blocked exactly the rebind it existed to allow.
   */
  #bound = false;

  connectedCallback() {
    this.variantId = this.dataset.variant;
    this.inCart = this.dataset.inCart === 'true';
    this.otherItems = Number(this.dataset.otherItems) || 0;
    this.autoAdd = this.dataset.autoAdd === 'true';
    if (!this.#bound) {
      this.#bound = true;
      this.addEventListener('click', (event) => {
        const hit = event.target.closest('[ref="toggle"]');
        if (hit && this.contains(hit)) this.#onToggle();
      });
    }

    // The drawer usually re-renders and remounts this element, but when it
    // morphs the DOM in place the element survives and connectedCallback never
    // fires again — which is how emptying the bag could leave protection
    // sitting there until a reload. Listening to the cart itself covers both.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChanged);

    this.#scheduleReconcile();
  }

  #scheduleReconcile() {
    clearTimeout(this.recheck);
    this.recheck = setTimeout(() => {
      this.#reconcile().catch(() => {
        /* a failed reconcile must not leave the element wedged */
        this.#setBusy(false);
      });
    }, 60);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartChanged);
    clearTimeout(this.recheck);
  }

  #onCartChanged = () => {
    // Ignore the echo of our own write.
    if (selfWrites > 0) return;
    // One reconcile per burst: a single removal can fire several updates as the
    // drawer, icon and totals all settle.
    this.#scheduleReconcile();
  };

  // Looked up on demand: a cached node points at whatever the morph discarded.
  get toggle() {
    return this.querySelector('[ref="toggle"]');
  }

  get msg() {
    return this.querySelector('[ref="msg"]');
  }

  get text() {
    return this.querySelector('[ref="text"]');
  }

  get #declined() {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch {
      return false;
    }
  }

  set #declined(value) {
    try {
      if (value) sessionStorage.setItem(DECLINED_KEY, '1');
      else sessionStorage.removeItem(DECLINED_KEY);
    } catch {
      /* private mode — fall back to session-less behaviour */
    }
  }

  get #offered() {
    try {
      return sessionStorage.getItem(OFFERED_KEY) === '1';
    } catch {
      return false;
    }
  }

  set #offered(value) {
    try {
      if (value) sessionStorage.setItem(OFFERED_KEY, '1');
      else sessionStorage.removeItem(OFFERED_KEY);
    } catch {
      /* private mode */
    }
  }

  /**
   * A flat charge is a flat charge. If anything has left more than one in the
   * cart — a double add, a stale render, a customer who found the stepper on a
   * page we don't control — pull it back to one rather than bill for two.
   * Runs on every mount, so an already-wrong cart heals itself on next load.
   */
  async #enforceSingle(line) {
    if (!line || line.quantity <= 1) return;
    try {
      await writeCart(this, changeUrl(), { id: line.key, quantity: 1 }, 'update');
    } catch {
      /* leave the cart as it is rather than risk making it worse */
    }
  }

  /** Keep the cart honest without the customer having to do anything. */
  async #reconcile() {
    // One read for the whole pass. This used to fetch the cart up to three
    // times — enforceSingle, the emptiness check and currentLine each did their
    // own — which on a phone is three round trips before anything moves.
    let cart;
    try {
      cart = await refreshCart();
    } catch {
      return;
    }

    const line = cart.items.find((i) => String(i.variant_id) === String(this.variantId)) || null;
    const others = cart.items.reduce(
      (n, i) => (String(i.variant_id) === String(this.variantId) ? n : n + i.quantity),
      0
    );

    // Trust the live cart over the attributes this element was rendered with:
    // a morphed drawer or a cached section render can both be out of date.
    this.otherItems = others;
    this.inCart = !!line;
    if (line) this.dataset.lineKey = line.key;

    await this.#enforceSingle(line);

    // Bag emptied of real products: drop the protection and forget the
    // session state, so the next bag starts clean with it switched on again.
    if (others === 0) {
      this.#declined = false;
      this.#offered = false;
      if (line) {
        await this.#remove({ silent: true });
        this.#paint(false);
      }
      return;
    }

    if (!this.autoAdd) return;

    // Trust the live cart, not the attribute this element was rendered with.
    // A drawer that morphed in place, or a cached section render, can claim
    // protection is absent when it is already there — and auto-adding on top
    // of that is exactly how a second one gets billed.
    if (line) {
      // Already on — remember that we've offered it, so a later removal
      // through the line item's own remove button is treated as a decline
      // rather than something to undo.
      this.#offered = true;
      this.#paint(true);
      return;
    }

    // Absent, and we've already added it once this session — the customer
    // took it off, by whichever control. Leave it alone.
    if (this.#declined || this.#offered) return;

    await this.#add({ silent: true });
    this.#offered = true;
    this.#paint(true);
  }

  /**
   * Reads the live cart rather than trusting the state this element was
   * rendered with. If the drawer morphs in place instead of being replaced,
   * cached state goes stale and the toggle gets stuck doing one thing.
   */
  async #currentLine() {
    try {
      const cart = await refreshCart();
      const line = cart.items.find((i) => String(i.variant_id) === String(this.variantId));
      return line ? { present: true, key: line.key } : { present: false, key: null };
    } catch {
      return { present: this.inCart, key: this.dataset.lineKey || null };
    }
  }

  async #onToggle() {
    if (this.busy) return;
    this.#setBusy(true);

    // try/finally is load-bearing. Any failure in here — a dropped connection,
    // a rate-limited cart write — used to throw straight out of the handler and
    // skip the final setBusy(false), leaving this.busy stuck true. Every later
    // click then hit the guard above and returned silently, so the switch was
    // dead for the rest of the page's life with nothing on screen to say why.
    try {
      const { present, key } = await this.#currentLine();

      if (present) {
        this.#declined = true;
        if (key) this.dataset.lineKey = key;
        await this.#remove({ keepBusy: true });
        this.#paint(false);
      } else {
        this.#declined = false;
        this.#offered = true;
        const ok = await this.#add({ keepBusy: true });
        this.#paint(!!ok);
        if (!ok && this.msg) this.msg.textContent = 'Could not add protection';
      }
    } catch {
      // Leave the switch showing whatever the cart actually says.
      if (this.msg) this.msg.textContent = 'Could not update protection';
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Reflect the new state immediately. The drawer re-render supersedes this,
   * but if it is delayed the switch still matches reality rather than sitting
   * in the old position — which is what made it feel stuck.
   */
  #paint(on) {
    this.inCart = on;
    this.dataset.inCart = String(on);
    this.classList.toggle('sc-prot--on', on);
    this.toggle?.setAttribute('aria-checked', String(on));

    const copy = on ? this.dataset.textOn : this.dataset.textOff;
    if (this.text && copy) this.text.textContent = copy;
    if (this.msg) this.msg.textContent = '';
  }

  #setBusy(state) {
    this.busy = state;
    if (this.toggle) this.toggle.disabled = state;
  }

  async #add({ silent, keepBusy } = {}) {
    if (!keepBusy) this.#setBusy(true);
    return serialise(async () => {

    // The description rides along as a line item property so it renders under
    // the title in the cart natively, and stays attached to the order.
    const label = this.dataset.propertyLabel;
    const value = this.dataset.propertyValue;
    const item = { id: Number(this.variantId), quantity: 1 };
    if (label && value) item.properties = { [label]: value };

      // Re-check inside the queue: another instance may have added it while
      // this call was waiting its turn.
      const { present } = await this.#currentLine();
      if (present) {
        if (!keepBusy) this.#setBusy(false);
        return true;
      }

      const ok = await writeCart(this, addUrl(), { items: [item] }, 'add');
      if (!keepBusy) this.#setBusy(false);
      if (!ok && !silent && this.msg) this.msg.textContent = 'Could not add protection';
      return ok;
    });
  }

  async #remove({ silent, keepBusy } = {}) {
    if (!keepBusy) this.#setBusy(true);
    return serialise(async () => {
      const key = this.dataset.lineKey || Number(this.variantId);
      const ok = await writeCart(this, changeUrl(), { id: key, quantity: 0 }, 'remove');
      if (!keepBusy) this.#setBusy(false);
      if (!ok && !silent && this.msg) this.msg.textContent = 'Could not remove protection';
      return ok;
    });
  }
}

if (!customElements.get('sc-protection')) {
  customElements.define('sc-protection', SCProtection);
}
